import { Component, createRef } from 'react';
import styles from './editor.module.scss';
import { EditorComponent } from './editor.component';
import { sniffFormat } from './codecs/detect';
import { readHead } from './codecs/canvas-utils';
import { landingFor, emptyStateCopy, LandingSpec } from './routing';
import { BATCH_LIMIT } from './model/batch';
import { pickUpTransferredFiles } from '../../utils/pickup-transferred-files';
import FileUploadComponent from '../converter/file-upload/file-upload.component';
import { SessionFilesComponent } from '../converter/session-files/session-files.component';
import { FeatureDetailsComponent } from '../ui/feature-details/feature-details.component';
import { RelatedLinksComponent } from '../ui/related-links/related-links.component';
import RatingDisplayComponent from '../ui/rating-display/rating-display.component';
import { MotiveComponent } from '../ui/motive/motive.component';
import { AdSlots } from '../ui/motive/ad-slots';
import { MotiveLocationTypes } from '../../utils/request.utils';
import { MetricsService } from '../../services/metrics.service';
import { container } from '../../context/module';
import { FileSelectEvent } from '../../types/file-upload.types';
import { PageData, RouteProps } from '../../types/route-types';
import { RouteUtils } from '../../utils/route.utils';

interface RootState {
    files: File[];
    dragOver: boolean;
    rejected: string | null;
}

/** Extensions the editor can open in the browser (design doc §4). */
const ACCEPTED = ['jpg', 'jpeg', 'jfif', 'png', 'apng', 'webp', 'gif', 'bmp', 'tiff', 'tif', 'ico', 'avif', 'heic', 'heif', 'psd', 'ppm'];

/**
 * Route entry for /image-resizer and every resize-* / crop-* / rotate-* / flip-* slug
 * (route-to-component-map.ts). Owns the file list; hands it to EditorComponent once there is one.
 * The empty state is the first thing a visitor sees, so it names the page's tool and format
 * (routing.ts) and offers files handed over from other tools and recent files.
 */
export default class EditorRootComponent extends Component<RouteProps, RootState> {
    private landing: LandingSpec;
    private title = '';
    private description = '';
    private feature = '';
    private fileInput = createRef<HTMLInputElement>();

    constructor(props: RouteProps) {
        super(props);
        this.landing = landingFor(props.path);
        this.feature = RouteUtils.getPathFromProps(props);
        const pd: PageData | undefined = props.pageData;
        this.title = pd?.page_seo?.title || '';
        this.description = (pd?.page_seo?.description || '').replace(/\s+/g, ' ');
        this.state = { files: [], dragOver: false, rejected: null };
    }

    componentDidMount() {
        pickUpTransferredFiles({ acceptedExtensions: ACCEPTED, pageSlug: this.landing.slug, stage: files => this.stage(files) });
        document.addEventListener('paste', this.onPaste);
        // Drop a photo anywhere on the page while the editor is open to add it (UX review N1b); the
        // empty-state zone handles its own drop first and marks the event handled.
        document.addEventListener('dragover', this.onDocDrag);
        document.addEventListener('drop', this.onDocDrop);
    }
    componentWillUnmount() {
        document.removeEventListener('paste', this.onPaste);
        document.removeEventListener('dragover', this.onDocDrag);
        document.removeEventListener('drop', this.onDocDrop);
    }
    private onDocDrag = (e: DragEvent) => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault(); };
    private onDocDrop = (e: DragEvent) => {
        if (e.defaultPrevented) return;
        const files = Array.from(e.dataTransfer?.files || []); if (!files.length) return;
        e.preventDefault();
        this.stage(files, this.state.files.length > 0);
    };
    /** Ctrl/⌘+V an image (a screenshot, a copied photo) opens it — the desktop power path (UX review N1). */
    private onPaste = (e: ClipboardEvent) => {
        const t = e.target as HTMLElement | null; if (t && /^(INPUT|TEXTAREA)$/.test(t.tagName)) return;
        const files = Array.from(e.clipboardData?.files || []).filter(f => f.type.startsWith('image/') || /\.(heic|heif|tif|tiff|psd|ppm|ico|avif)$/i.test(f.name));
        if (!files.length) return;
        e.preventDefault();
        this.stage(files, this.state.files.length > 0);
        this.telemetry('EDITOR_PICKER_OPENED', { tool: this.landing.tool, via: 'paste' });
    };

    private telemetry = (event: string, attrs?: Record<string, unknown>) => {
        try { container.get(MetricsService).emit(event as any, { page: this.landing.slug, ...(attrs || {}) }); } catch { /* telemetry never blocks */ }
    };

    /** The single path every file takes in: picker, drop, hand-off, recent. Filters by extension, caps the batch. */
    private async stage(incoming: File[], append = false) {
        const ext = (f: File) => (f.name.split('.').pop() || '').toLowerCase();
        // Accept by extension OR MIME type: Android's photo picker hands files a content-provider
        // name like "image:1000047842" with no extension but a correct type ("image/jpeg"), which
        // extension-only filtering wrongly rejected (QA on a phone, 2026-09-06). Any image/* type,
        // plus the HEIC/HEIF/PSD types browsers spell inconsistently, is allowed in; the codec
        // sniffs the real format from the bytes and fails gracefully if it truly can't open it.
        const okType = (f: File) => /^image\//i.test(f.type) || /(heif|heic|photoshop|x-icon|vnd\.microsoft\.icon)/i.test(f.type);
        const takes = (f: File) => ACCEPTED.includes(ext(f)) || okType(f);
        // Third case (owner's phone, 2026-09-06): the Files app's "Images" category hands over
        // "image%3A1000047842" with NO extension and an EMPTY type for a plain JPEG. Sniff the first
        // 32 bytes; if it is a format we open, wrap it in a File with a real name + type so the codec
        // dispatch and the download name both work ("photo-1000047842.jpg").
        const resolved = await Promise.all(incoming.map(async f => {
            if (takes(f)) return f;
            try {
                const fmt = sniffFormat(await readHead(f));
                if (fmt !== 'unknown' && fmt !== 'raw' && fmt !== 'eps' && ACCEPTED.includes(fmt)) {
                    const base = (decodeURIComponent(f.name).replace(/^image:/i, 'photo-').replace(/[^\w.-]+/g, '-').replace(/\.[^.]*$/, '') || 'photo');
                    return new File([f], `${base}.${fmt}`, { type: `image/${fmt === 'jpg' ? 'jpeg' : fmt}`, lastModified: f.lastModified });
                }
            } catch { /* unreadable: falls through to the skip message */ }
            return f;
        }));
        const accepted = resolved.filter(takes);
        const skipped = resolved.filter(f => !takes(f)).map(f => f.name);
        const files = (append ? [...this.state.files, ...accepted] : accepted).slice(0, BATCH_LIMIT);
        const over = (append ? this.state.files.length : 0) + accepted.length - files.length;
        const rejected = skipped.length
            ? `Skipped ${skipped.length === 1 ? skipped[0] : skipped.length + ' files'}: this editor opens JPG, PNG, WebP, GIF, BMP, TIFF, HEIC, AVIF, ICO, PSD and PPM. RAW and EPS files can be converted first with the converter pages.`
            : over > 0 ? `Only the first ${BATCH_LIMIT} files were added.` : null;
        this.setState({ files, rejected, dragOver: false });
        if (accepted.length) this.telemetry('EDITOR_FILES_STAGED', { count: files.length, tool: this.landing.tool });
    }

    private onSelect = (ev: FileSelectEvent, append = false) => {
        const files = (ev.files || []).filter((f): f is File => typeof File !== 'undefined' && f instanceof File);
        if (files.length) this.stage(files, append);
    };

    private onDrop = (e: React.DragEvent) => { e.preventDefault(); const files = Array.from(e.dataTransfer?.files || []); if (files.length) this.stage(files, this.state.files.length > 0); };
    private onDrag = (over: boolean) => (e: React.DragEvent) => { e.preventDefault(); if (this.state.dragOver !== over) this.setState({ dragOver: over }); };

    private openPicker = () => this.fileInput.current?.click();

    render() {
        const { files } = this.state;
        return <div className={styles.root}>
            {files.length === 0 ? this.emptyState() :
                <EditorComponent key={files.map(f => f.name + f.size).join('|')} files={files} landing={this.landing}
                    onChangeFiles={() => this.setState({ files: [] })} onAddMore={this.openPicker} onTelemetry={this.telemetry} />}
            {/* hidden multi-file input for "Add more" while the editor is open */}
            <input ref={this.fileInput} type="file" multiple accept="image/*,.heic,.heif,.tif,.tiff,.psd,.ppm,.ico,.avif" style={{ display: 'none' }}
                onChange={e => { const fs = Array.from(e.target.files || []); if (fs.length) this.stage(fs, true); e.target.value = ''; }} />
            {this.seoContent()}
        </div>;
    }

    private emptyState() {
        const copy = emptyStateCopy(this.landing);
        const heading = this.title || `${copy.verb} ${copy.noun}`;
        return <div className={styles.empty} data-testid="editor-empty">
            <div>
                <h1>{heading}</h1>
                {this.description && <p className={styles.sub}>{this.description}</p>}
            </div>
            <div className={`${styles.drop} ${this.state.dragOver ? styles.over : ''}`} onDrop={this.onDrop} onDragOver={this.onDrag(true)} onDragEnter={this.onDrag(true)} onDragLeave={this.onDrag(false)} data-testid="drop-zone">
                <FileUploadComponent {...this.props}
                    fileSelected={(ev: FileSelectEvent) => this.onSelect(ev)}
                    fileSelectButtonClicked={() => this.telemetry('EDITOR_PICKER_OPENED', { tool: this.landing.tool })}
                    shouldDisplayBtnsAsPrimary={true}
                    acceptedFileTypes="image/*,.heic,.heif,.tif,.tiff,.psd,.ppm,.ico,.avif"
                    uploadBtnText={copy.button} showGoogleDrive={false} showDropbox={false}
                    uploadBtnClassName="btn-primary">
                </FileUploadComponent>
                <div>or drop photos here · up to {BATCH_LIMIT} at once</div>
                <div className={styles.fmts}><b>JPG, PNG, WebP, GIF, BMP, TIFF, HEIC, AVIF, ICO, PSD, PPM.</b> Everything happens in your browser; nothing is uploaded.</div>
            </div>
            {this.state.rejected && <div className={styles.errorNote} role="alert">{this.state.rejected}</div>}
            <SessionFilesComponent acceptedExtensions={ACCEPTED} pageSlug={this.landing.slug} toolName={`${copy.verb} ${copy.noun}`} pageData={this.props.pageData}
                onPick={(picked: File[]) => this.stage(picked)} />
            <div className="gcontent-container">
                <MotiveComponent data={AdSlots.pageTop} containerWidth="100%" containerHeight="90px" location={MotiveLocationTypes.convertPageTop} adWidth="970px" adHeight="90px" showAds={true} className="width_major" />
            </div>
        </div>;
    }

    /** Same three blocks as ConverterPageBase, inside the same 1000px shell (kept from the v1 fix). */
    private seoContent() {
        return <div className={`col-12 ${styles.empty}`} style={{ paddingTop: 0 }}>
            {this.props.markdown && <FeatureDetailsComponent markdown={this.props.markdown}></FeatureDetailsComponent>}
            <RelatedLinksComponent relatedLinks={this.props.relatedLinks}></RelatedLinksComponent>
            <RatingDisplayComponent rating={this.props.rating} title={this.title} feature={this.feature} description={this.description}></RatingDisplayComponent>
        </div>;
    }
}
